import { useEffect, useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { 
  User, 
  Mail, 
  Building, 
  MapPin, 
  Globe, 
  Clock,
  Camera,
  Save,
  RefreshCw
} from 'lucide-react'
import { toast } from 'sonner'

const timezones = [
  'UTC',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney'
]

export function ProfileSettings() {
  const { user } = useAuthStore()
  const { profile, updateProfile, loading } = useSettingsStore()
  const [formData, setFormData] = useState({
    name: profile.name || user?.name || '',
    email: profile.email || user?.email || '',
    bio: profile.bio || '',
    company: profile.company || '',
    location: profile.location || '',
    website: profile.website || '',
    timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  })
  const [hasChanges, setHasChanges] = useState(false)

  // Keep form in sync when store profile or auth user changes (e.g., after loadSettings)
  useEffect(() => {
    setFormData({
      name: profile.name || user?.name || '',
      email: profile.email || user?.email || '',
      bio: profile.bio || '',
      company: profile.company || '',
      location: profile.location || '',
      website: profile.website || '',
      timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    setHasChanges(false)
  }, [profile.name, profile.email, profile.bio, profile.company, profile.location, profile.website, profile.timezone, user?.name, user?.email])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      await updateProfile(formData)
      setHasChanges(false)
      toast.success('Profile updated successfully!')
    } catch (error) {
      toast.error('Failed to update profile')
    }
  }

  const handleReset = () => {
    setFormData({
      name: profile.name || user?.name || '',
      email: profile.email || user?.email || '',
      bio: profile.bio || '',
      company: profile.company || '',
      location: profile.location || '',
      website: profile.website || '',
      timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    })
    setHasChanges(false)
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Profile Information</span>
          </CardTitle>
          <CardDescription>
            Update your personal information and public profile details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <Avatar className="h-24 w-24">
                <AvatarImage src={profile.avatar} />
                <AvatarFallback className="text-lg font-semibold">
                  {getInitials(formData.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <Button variant="outline" size="sm" className="flex items-center space-x-2">
                <Camera className="h-4 w-4" />
                <span>Change Photo</span>
              </Button>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">JPG, PNG or GIF</p>
                <p className="text-xs text-muted-foreground">Max size: 5MB</p>
              </div>
            </div>

            {/* Profile Form */}
            <form onSubmit={handleSubmit} className="flex-1 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell us about yourself..."
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Brief description for your profile. This will be visible to your team members.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    placeholder="Your company name"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    placeholder="City, Country"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://yourwebsite.com"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select value={formData.timezone} onValueChange={(value) => handleInputChange('timezone', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <div className="flex items-center space-x-2">
                  {hasChanges && (
                    <Badge variant="outline" className="text-orange-600">
                      Unsaved changes
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {hasChanges && (
                    <Button type="button" variant="outline" onClick={handleReset}>
                      Reset
                    </Button>
                  )}
                  <Button 
                    type="submit" 
                    disabled={!hasChanges || loading}
                    className="flex items-center space-x-2"
                  >
                    {loading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Contact Information</span>
          </CardTitle>
          <CardDescription>
            How others can reach you
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <div className="font-medium">{formData.email}</div>
                <div className="text-sm text-muted-foreground">Primary email address</div>
              </div>
              <Badge variant="outline">Verified</Badge>
            </div>
            
            {formData.company && (
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <Building className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">{formData.company}</div>
                  <div className="text-sm text-muted-foreground">Company</div>
                </div>
              </div>
            )}
            
            {formData.location && (
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">{formData.location}</div>
                  <div className="text-sm text-muted-foreground">Location</div>
                </div>
              </div>
            )}
            
            {formData.website && (
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium">
                    <a 
                      href={formData.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {formData.website}
                    </a>
                  </div>
                  <div className="text-sm text-muted-foreground">Website</div>
                </div>
              </div>
            )}
            
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <div className="font-medium">{formData.timezone}</div>
                <div className="text-sm text-muted-foreground">
                  Current time: {new Date().toLocaleTimeString('en-US', { timeZone: formData.timezone })}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 